import Button from "antd/es/button"
import Form from "antd/es/form"
import Select from "antd/es/select"
import Switch from "antd/es/switch"
import { useCallback, useState } from "react"

import useErrorPopup from "../hooks/useErrorPopup"
import { updateSlackConfiguration } from "../slack/api"
import type { SlackChannel, SlackInstallationMeta } from "../slack/apiTypes"

interface FormDataType {
  enabledOnAllChannels: boolean
  selectedChannels: string[]
}

const getInitialValues = (
  slackInstallation?: SlackInstallationMeta,
): FormDataType => {
  if (!slackInstallation) {
    return {
      enabledOnAllChannels: true,
      selectedChannels: [],
    }
  }
  return {
    enabledOnAllChannels: slackInstallation.configuration.enabledOnAllChannels,
    selectedChannels: Object.keys(
      slackInstallation.configuration.channels,
    ).filter((id) => slackInstallation.configuration.channels[id].enabled),
  }
}

const formDataToSlackConfig = (
  formData: FormDataType,
): SlackInstallationMeta["configuration"] => {
  const { enabledOnAllChannels, selectedChannels } = formData
  if (enabledOnAllChannels) {
    return {
      enabledOnAllChannels,
      channels: {},
    }
  }
  return {
    enabledOnAllChannels,
    channels: selectedChannels.reduce<{ [id: string]: { enabled: boolean } }>(
      (accum, id) => {
        accum[id] = { enabled: true }
        return accum
      },
      {},
    ),
  }
}

interface SlackConfigurationFormProps {
  slackInstallation?: SlackInstallationMeta
  channels: SlackChannel[] | undefined
  refetchSlackInstallation: () => Promise<void>
  onClose: () => void
  loading: boolean
  error: Error | null
}

const SlackConfigurationForm: React.FC<SlackConfigurationFormProps> = ({
  slackInstallation,
  channels,
  refetchSlackInstallation,
  onClose,
  loading,
  error,
}) => {
  const [form] = Form.useForm<FormDataType>()
  const { handleError, handleSuccess } = useErrorPopup()
  const [formSubmitting, setFormSubmitting] = useState(false)

  const onFormSubmit = useCallback(
    async (formValues: FormDataType) => {
      if (!channels) {
        return
      }
      setFormSubmitting(true)
      try {
        await updateSlackConfiguration({
          newConfig: formDataToSlackConfig(formValues),
        })
        await refetchSlackInstallation()
        handleSuccess("Updated Slack Configuration")
        onClose()
      } catch (error) {
        handleError({
          error,
          prefix: "Unable to update Slack Configuration",
        })
      } finally {
        setFormSubmitting(false)
      }
    },
    [handleError, handleSuccess, refetchSlackInstallation, onClose, channels],
  )

  const enabledOnAllChannels = Form.useWatch("enabledOnAllChannels", form)
  const channelOptions = channels?.map((channel) => ({
    label: channel.name,
    value: channel.id,
  }))
  return (
    <Form
      className="min-w-[500px] max-w-[700px]"
      form={form}
      initialValues={getInitialValues(slackInstallation)}
      disabled={formSubmitting || loading}
      onFinish={onFormSubmit}
      layout="vertical"
    >
      {error && <div>Error loading data: {error.message}</div>}
      <Form.Item
        layout="vertical"
        tooltip="NOTE: The bot must be manually added to private channels in Slack. Ask Quilt for help setting this up."
        label="Enable access to all channels"
        name="enabledOnAllChannels"
        valuePropName="checked"
        className={`mb-3 rounded-lg p-3 ${enabledOnAllChannels ? "bg-purple-25" : "bg-gray-25"}`}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        label="Specify Accessible Channels"
        tooltip={
          <>
            The bot will respond in these channels when you mention it with
            @Quilt.
            <br />
            If a channel is missing, please contact Quilt support.
          </>
        }
        name="selectedChannels"
        colon={false}
      >
        <Select<string[]>
          mode="multiple"
          placeholder={
            enabledOnAllChannels ? "All channels enabled" : "Select channels"
          }
          options={channelOptions}
          optionFilterProp="label"
          loading={formSubmitting || loading}
          disabled={enabledOnAllChannels}
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={formSubmitting}>
          Save Configuration
        </Button>
      </Form.Item>
    </Form>
  )
}

export default SlackConfigurationForm
